import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomButton } from "../../../atoms/CustomButton";
import { NoTranslate } from "../../../atoms/NoTranslate";
import PartnerAdminService, {
  UpgradeSettlement,
} from "../../../../services/partnerAdmin";
import { formatAmount, formatDay, formatPayModDay } from "./partnerLabels";

const FIELD =
  "w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm";

interface UpgradeSettlementsProps {
  customerId: string;
  entityId: string;
  onOpenPartner: (partnerId: string) => void;
}

const errorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "Request failed";

/**
 * Upgrades a distributor bought: the client runs on both plans until root cancels the old one and refunds the part
 * of its year left. The suggested refund is a prorata root checks and can change.
 */
const UpgradeSettlements: React.FC<UpgradeSettlementsProps> = ({
  customerId,
  entityId,
  onOpenPartner,
}) => {
  const [settlements, setSettlements] = useState<UpgradeSettlement[]>([]);
  const [target, setTarget] = useState<UpgradeSettlement | null>(null);
  const [refund, setRefund] = useState("");
  const [reason, setReason] = useState("");
  const [settling, setSettling] = useState(false);

  const load = async () => {
    try {
      setSettlements(
        await PartnerAdminService.settlements(customerId, entityId)
      );
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  useEffect(() => {
    if (customerId && entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, entityId]);

  const pending = settlements.filter(
    (settlement) => settlement.status === "pending"
  );
  if (pending.length === 0) return null;

  const open = (settlement: UpgradeSettlement) => {
    setTarget(settlement);
    setRefund(
      settlement.suggestion
        ? (settlement.suggestion.suggested_refund / 100).toFixed(2)
        : ""
    );
    setReason("Upgrade to a higher plan");
  };

  const cents = Math.round(Number(refund.replace(",", ".")) * 100);
  const valid = refund.trim() !== "" && Number.isFinite(cents) && cents >= 0;

  const settle = async () => {
    if (!target) return;
    setSettling(true);
    try {
      await PartnerAdminService.settle(
        customerId,
        entityId,
        target.old_subscription_id,
        {
          refund_amount: cents,
          reason: reason.trim(),
        }
      );
      toast.success("Old plan cancelled and refunded");
      setTarget(null);
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSettling(false);
    }
  };

  const currency = target?.suggestion?.currency ?? "";

  return (
    <section className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="mb-3 text-sm font-semibold text-gray-900">
        Upgrades to settle{" "}
        <span className="tabular-nums">({pending.length})</span>
      </p>
      <ul className="divide-y divide-amber-100 rounded-lg border border-amber-200 bg-white">
        {pending.map((settlement) => (
          <li
            key={settlement.old_subscription_id}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center"
          >
            <div className="min-w-0 flex-1 text-xs text-gray-600">
              <p className="text-sm font-medium text-gray-900">
                <NoTranslate>{settlement.entity_id}</NoTranslate>
              </p>
              <p>
                <NoTranslate>{settlement.old_plan_code}</NoTranslate> replaced
                by <NoTranslate>{settlement.new_plan_code}</NoTranslate> on{" "}
                {formatDay(settlement.created_at)}
                {settlement.suggestion
                  ? ` · ${
                      settlement.suggestion.days_left
                    } days left, suggested refund ${formatAmount(
                      settlement.suggestion.suggested_refund / 100,
                      settlement.suggestion.currency
                    )}`
                  : " · paid invoices unavailable"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <CustomButton
                variant="secondary"
                size="xs"
                title="Distributor"
                onClick={() => onOpenPartner(settlement.partner_id)}
              />
              <CustomButton
                variant="primary"
                size="xs"
                title="Settle"
                onClick={() => open(settlement)}
              />
            </div>
          </li>
        ))}
      </ul>

      <ModalShell
        show={Boolean(target)}
        onClose={() => setTarget(null)}
        title="Settle this upgrade"
        subtitle={target?.old_subscription_id}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <CustomButton
              variant="secondary"
              title="Cancel"
              onClick={() => setTarget(null)}
            />
            <CustomButton
              variant="warning"
              title="Cancel the old plan and refund"
              disabled={!valid || reason.trim().length < 3 || settling}
              onClick={settle}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-700">
            The old plan is cancelled at once and the amount below is refunded
            to the distributor&apos;s card. The rebate of the old plan stays
            acquired.
          </p>
          {target?.suggestion && (
            <p className="text-xs text-gray-500">
              {`Paid ${formatAmount(
                target.suggestion.amount_paid / 100,
                target.suggestion.currency
              )} for ${formatPayModDay(
                target.suggestion.period_start
              )} to ${formatPayModDay(target.suggestion.period_end)}, ${
                target.suggestion.days_left
              } days left.`}
            </p>
          )}
          <label className="text-xs text-gray-500">
            {`Refund${currency ? ` in ${currency}` : ""}, 0 for none`}
            <input
              className={FIELD}
              inputMode="decimal"
              value={refund}
              onChange={(e) => setRefund(e.target.value)}
            />
          </label>
          <textarea
            className={FIELD}
            rows={2}
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </ModalShell>
    </section>
  );
};

export default UpgradeSettlements;
